import { ChangeEventHandler, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Page, Content, HeaderLabel } from '@backstage/core-components';
import { configApiRef, featureFlagsApiRef, githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { GithubSession, MockEmuGithubAuthAdapter, MockGithubAuthApi } from '../../services';
import { Button, Link, TextField } from '@material-ui/core';
import { Config } from '@backstage/config';
import { Octokit } from '@octokit/rest';
import { RunwaySupportInformation } from '../SupportInformation/SupportInformation';
import { emuAuthApiRef } from '../../refs';

const getGitHubLink = () => {
  return 'https://github.com/settings/tokens/new';
};

const getScopes = () => {
  return ['repo', 'workflow', 'user', 'delete_repo'];
};

const getGhApiUrl = (config: Config) => {
  return config.getString('github.ghec.apiUrl');
};

export const DevGitHubLogin = () => {
  const githubAuth = useApi(githubAuthApiRef);
  const emuAuth = useApi(emuAuthApiRef);
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const isEmuEnabled = featureFlagsApi.isActive('github-emu-auth');
  const config = useApi(configApiRef);
  const [githubToken, setGithubToken] = useState('');
  const [emuToken, setEmuToken] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const saveTokenSession = async (authApi: MockGithubAuthApi | MockEmuGithubAuthAdapter, token: string) => {
    const octokit = new Octokit({
      auth: token,
      baseUrl: getGhApiUrl(config),
    });
    const user = await octokit.users.getAuthenticated();

    const session: GithubSession = {
      providerInfo: {
        accessToken: token,
        scopes: new Set(getScopes()),
        expiresAt: new Date(),
      },
      profile: {},
      backstageIdentity: {
        identity: {
          ownershipEntityRefs: [`user:default/${user.data.login}`],
          type: 'user',
          userEntityRef: `user:default/${user.data.login}`,
        },
        token: 'test',
      },
    };

    authApi.saveSession(session);
  };

  const providersNeedingToken = useMemo(() => {
    const providerConfigs: {
      id: string;
      label: string;
      hasSession: boolean;
      token: string;
      authApi: MockGithubAuthApi | MockEmuGithubAuthAdapter;
    }[] = [];

    if (githubAuth instanceof MockGithubAuthApi) {
      providerConfigs.push({
        id: 'ghec',
        label: '(GHEC)',
        hasSession: githubAuth.hasSession(),
        token: githubToken,
        authApi: githubAuth,
      });
    }

    if (isEmuEnabled && emuAuth instanceof MockEmuGithubAuthAdapter) {
      providerConfigs.push({
        id: 'emu',
        label: '(EMU)',
        hasSession: emuAuth.hasSession(),
        token: emuToken,
        authApi: emuAuth,
      });
    }

    return providerConfigs.filter(provider => !provider.hasSession);
  }, [emuAuth, emuToken, githubAuth, githubToken, isEmuEnabled]);

  const handleSubmit = async () => {
    if (providersNeedingToken.length === 0) {
      navigate(-1);
      return;
    }

    const missingTokenProvider = providersNeedingToken.find(provider => provider.token.trim().length === 0);
    if (missingTokenProvider) {
      setError(`Enter a personal access token for ${missingTokenProvider.label}.`);
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      await Promise.all(
        providersNeedingToken.map(provider => saveTokenSession(provider.authApi, provider.token.trim())),
      );
    } catch (err) {
      setError(
        `Unable to validate one or more tokens. Verify the token(s) and try again. ${err instanceof Error ? err.message : ''}`,
      );
      setIsSubmitting(false);
      return;
    }

    navigate(-1);
  };
  const handleGitHubTokenChange: ChangeEventHandler<HTMLInputElement> = event => {
    setGithubToken(event.target.value);
  };

  const handleEmuTokenChange: ChangeEventHandler<HTMLInputElement> = event => {
    setEmuToken(event.target.value);
  };

  return (
    <Page themeId="home">
      <Header title="Set GitHub Token" subtitle="">
        <HeaderLabel label="Owner" value="Runway Metrics Team" />
        <HeaderLabel label="Lifecycle" value="Alpha" />
      </Header>
      <Content>
        <RunwaySupportInformation />
        {providersNeedingToken.length > 0 && (
          <>
            Create a personal access token(PAT) on
            <Link target="_blank" href={getGitHubLink()}>
              {' '}
              GitHub{' '}
            </Link>
            with the following scopes:
            <ul>
              {getScopes().map(scope => (
                <li key={scope}>{scope}</li>
              ))}
            </ul>
          </>
        )}
        <form
          onSubmit={e => {
            handleSubmit();
            e.preventDefault();
          }}
        >
          {providersNeedingToken.map(provider => (
            <div key={provider.id}>
              <TextField
                id={`${provider.id}-pat`}
                label={`${provider.label} PAT`}
                variant="outlined"
                type="password"
                value={provider.id === 'ghec' ? githubToken : emuToken}
                onChange={provider.id === 'ghec' ? handleGitHubTokenChange : handleEmuTokenChange}
              />
              <br />
              <br />
            </div>
          ))}
          {providersNeedingToken.length === 0 && <p>No additional GitHub tokens are needed right now.</p>}
          {error && <p>{error}</p>}
          <br />
          <br />
          <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
            {providersNeedingToken.length <= 1 ? 'Set Token' : 'Set Tokens'}
          </Button>
        </form>
      </Content>
    </Page>
  );
};
